import React, { useContext, useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { Link as RouterLink, useNavigate } from 'react-router';
import {
  Button,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Typography
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useSnackbar } from 'notistack';

import { LinkIcon } from '../../../common/icons/LinkIcon';
import { SupportIcon } from '../../../common/icons/SupportIcon';
import { LogoutIcon } from '../../../common/icons/LogoutIcon';
import StackVersions from '@/components/StackVersions';
import { ResetIcon } from '../../../common/icons/ResetIcon';
import AuthUserStore from '@/common/utils/AuthUser';
import CONFIG from '@/config';
import { ENV_PRODUCTION, RuntimeInfoContext } from '@/context/RuntimeInfo';
import { LockIcon } from '../../../common/icons/LockIcon';
import { useLock } from '@/context/LockContext';
import ChangeStudio from './ChangeStudio';
import AuthModal, { authModalStyles } from '@/RestoreComponent/AuthModal';
import { formDataInstance } from '@/common/axios';
import { getRestHeaders } from '@/common/utils/api';

interface NavMenuLinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  href: string;
}

const NavMenuLink: React.FC<NavMenuLinkProps> = ({
  href,
  children,
  ...props
}) => {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{ textDecoration: 'none', color: 'inherit' }}
      {...props}
    >
      {children}
    </a>
  );
};

export const navMenuStyles: any = makeStyles(() => ({
  menu: {
    //paddingTop: 0,
    //paddingBottom: 0
  },
  item: {
    height: '48px',
    width: '350px',
    fontSize: '16px',
    fontWeight: 400,
    '&.tall': {
      height: '58px'
    }
  },
  endAdornment: {
    marginLeft: 'auto',
    paddingLeft: '25px',
    color: '#888888'
  },
  divider: {
    margin: '8px 0px !important'
  },
  keyTip: {
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '12px',
    color: '#999999',
    padding: '4px',
    borderRadius: '2px',
    backgroundColor: '#ECECEC'
  }
}));

const NavMenu = props => {
  const { anchorEl, onClose, isNavOpen } = props;
  const classes = navMenuStyles();
  const authModalClasses = authModalStyles();
  const loggedInUser = AuthUserStore.getAuthUser();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { userLockPow } = useLock();

  const [userStores, setUserStores] = useState([]);

  // Logout
  const handleLogout = async () => {
    try {
      AuthUserStore.logout();
      enqueueSnackbar('Successfully logout!', { variant: 'success' });
      onClose();
    } catch {
      // just don't crash
    }
  };

  const getAvailableStores = async () => {
    formDataInstance
      .get(CONFIG.USERS_API_URL + `/staff-management/staff-stores`)
      .then(response => {
        setUserStores(response.data.data.stores);
      })
      .catch(error => {
        console.error(error);
      });
  };

  useEffect(() => {
    getAvailableStores();
  }, [loggedInUser.id]);

  // Support menu
  const [supMenuAnchorEl, setSupMenuAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const supMenuOpen = Boolean(supMenuAnchorEl);
  const runtimeInfo = useContext(RuntimeInfoContext);
  const showDevTools = runtimeInfo.environment !== ENV_PRODUCTION;
  const isRemote = localStorage.getItem('server') !== 'local';

  const handleSubOpen = event => {
    setSupMenuAnchorEl(event.currentTarget);
  };

  const handleSubClose = () => {
    setSupMenuAnchorEl(null);
  };

  const [isResetPinOpen, setIsResetPinOpen] = useState(false);
  const [isResetPinLoading, setIsResetPinLoading] = useState(false);

  const handleResetPin = async () => {
    setIsResetPinLoading(true);
    await formDataInstance
      .post(
        CONFIG.USERS_API_URL + '/staff-management/reset-pin',
        {},
        { headers: getRestHeaders() }
      )
      .then((response: any) => {
        if (response.data.data.success) {
          setIsResetPinOpen(false);
          AuthUserStore.logout();
        }
      })
      .catch(err => {
        console.log(err);
        enqueueSnackbar('There was an error sending link', {
          variant: 'error'
        });
      })
      .finally(() => {
        setIsResetPinLoading(false);
      });
  };

  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false);
  const [isResetPasswordLoading, setIsResetPasswordLoading] = useState(false);

  const handleResetPassword = async () => {
    setIsResetPasswordLoading(true);
    await formDataInstance
      .post(
        CONFIG.USERS_API_URL + '/auth/staff/request-password-reset',
        { email: loggedInUser.email },
        { headers: getRestHeaders() }
      )
      .then((response: any) => {
        if (response.data.data.success) {
          setIsResetPasswordOpen(false);
          AuthUserStore.logout();
        }
      })
      .catch(err => {
        console.log(err);
        enqueueSnackbar('There was an error sending link', {
          variant: 'error'
        });
      })
      .finally(() => {
        setIsResetPasswordLoading(false);
      });
  };

  // Lock POW
  const handleLock = () => {
    userLockPow();
    onClose();
  };

  // Report bug
  const bugReportUrl =
    'https://restoreportal.atlassian.net/servicedesk/customer/portal/27/group/58/create/500';

  // Backend logs
  const jwtToken = AuthUserStore.getAuthToken();

  // Remote backend
  const toggleRemoteBackend = () => {
    isRemote
      ? localStorage.setItem('server', 'local')
      : localStorage.setItem('server', 'remote');
    window.location.reload();
  };

  // Reset session
  const handleSessionReset = () => {
    window.localStorage.clear();
    navigate('/auth/login');
  };

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        anchorReference="anchorPosition"
        anchorPosition={{ top: -100, left: isNavOpen ? 230 : 50 }}
        open={menuOpen}
        onClose={onClose}
        autoFocus={false}
        disableAutoFocusItem
        classes={{ list: classes.menu }}
        PaperProps={{
          style: {
            backgroundColor: 'white',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
            borderRadius: 4
          }
        }}
        BackdropProps={{
          style: { backgroundColor: 'transparent' }
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
      >
        <ChangeStudio stores={userStores} />
        <NavMenuLink href={loggedInUser?.store?.url}>
          <MenuItem className={classes.item} onClick={onClose}>
            <ListItemIcon>
              <LinkIcon />
            </ListItemIcon>
            Go to Studio page
          </MenuItem>
        </NavMenuLink>
        {/*
      <NavMenuLink href="https://powtraining.restoreportal.com">
        <MenuItem className={classes.item} onClick={onClose}>
          <ListItemIcon>
            <LinkIcon />
          </ListItemIcon>
          Go to POW Training
        </MenuItem>
      </NavMenuLink>
      */}
        <NavMenuLink href="https://restoreportal.atlassian.net/servicedesk/customer/portals">
          <MenuItem className={classes.item} onClick={onClose}>
            <ListItemIcon>
              <LinkIcon />
            </ListItemIcon>
            Know More Do More Center
          </MenuItem>
        </NavMenuLink>
        <MenuItem
          className={classes.item}
          onClick={handleSubOpen}
          aria-haspopup="true"
          aria-controls="support-submenu"
        >
          <ListItemIcon>
            <SupportIcon />
          </ListItemIcon>
          Support
          <ListItemIcon className={classes.endAdornment}>
            <ChevronRightIcon />
          </ListItemIcon>
        </MenuItem>
        {!loggedInUser?.isGeneric && (
          <MenuItem
            className={classes.item}
            onClick={() => setIsResetPinOpen(true)}
          >
            <ListItemIcon>
              <ResetIcon />
            </ListItemIcon>
            Reset my PIN
          </MenuItem>
        )}
        {!loggedInUser?.isGeneric && (
          <MenuItem
            className={classes.item}
            onClick={() => setIsResetPasswordOpen(true)}
          >
            <ListItemIcon>
              <ResetIcon />
            </ListItemIcon>
            Reset my password
          </MenuItem>
        )}
        {!loggedInUser?.isGeneric && (
          <MenuItem className={classes.item} onClick={handleLock}>
            <ListItemIcon>
              <LockIcon />
            </ListItemIcon>
            Lock screen
            <Stack
              className={classes.endAdornment}
              direction="row"
              spacing={0.5}
            >
              <Typography className={classes.keyTip}>SHIFT</Typography>
              <Typography className={classes.keyTip}>ESC</Typography>
            </Stack>
          </MenuItem>
        )}
        <MenuItem className={classes.item} onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          Log out
        </MenuItem>
        <Divider className={classes.divider} />
        <StackVersions showLabel={false} />

        {/* Support Submenu */}
        <Menu
          id="support-submenu"
          anchorEl={supMenuAnchorEl}
          open={supMenuOpen}
          onClose={handleSubClose}
          disableAutoFocusItem
          classes={{ list: classes.menu }}
          PaperProps={{
            style: {
              backgroundColor: 'white',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
              borderRadius: 4
            }
          }}
          BackdropProps={{
            style: { backgroundColor: 'transparent' }
          }}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
        >
          <NavMenuLink href={bugReportUrl}>
            <MenuItem className={classes.item} onClick={handleSubClose}>
              <ListItemIcon>
                <LinkIcon />
              </ListItemIcon>
              Report bug
            </MenuItem>
          </NavMenuLink>
          {showDevTools && (
            <>
              <MenuItem
                className={classes.item}
                component={RouterLink}
                to="/logs/tracking"
                target="_blank"
                onClick={handleSubClose}
              >
                <ListItemIcon>
                  <LinkIcon />
                </ListItemIcon>
                Frontend logs
              </MenuItem>
              <NavMenuLink
                href={`${CONFIG.RETAIL_SERVICES}/reports/get_backend_log_errors?jwt=${jwtToken}&minutes=60`}
              >
                <MenuItem className={classes.item} onClick={handleSubClose}>
                  <ListItemIcon>
                    <LinkIcon />
                  </ListItemIcon>
                  Backend logs
                </MenuItem>
              </NavMenuLink>
              <Divider className={classes.divider} />
              <MenuItem className={classes.item} onClick={toggleRemoteBackend}>
                Remote backend
                <ListItemIcon className={classes.endAdornment}>
                  {isRemote ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
                </ListItemIcon>
              </MenuItem>
            </>
          )}
          <Divider className={classes.divider} />
          <MenuItem className={classes.item} onClick={handleSessionReset}>
            <ListItemIcon>
              <ResetIcon />
            </ListItemIcon>
            Reset session
          </MenuItem>
        </Menu>
      </Menu>

      {/* Reset PIN */}
      <AuthModal
        isOpen={isResetPinOpen}
        onClose={() => setIsResetPinOpen(false)}
        type="message"
      >
        <Typography className={authModalClasses.header} mb={2}>
          Reset my PIN
        </Typography>
        <Typography className={authModalClasses.message}>
          Are you sure you want to reset your PIN? A new PIN will be randomly
          generated and sent to your email.
        </Typography>
        <Typography className={authModalClasses.message}>
          Note: Clicking “Reset” will sign out of this session.
        </Typography>
        <Stack direction="row" justifyContent="flex-end" spacing={2} mt={2}>
          <Button
            variant="contained"
            className={authModalClasses.cancelButton}
            onClick={() => setIsResetPinOpen(false)}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            className={authModalClasses.submitButton}
            onClick={handleResetPin}
            loading={isResetPinLoading}
          >
            Reset
          </LoadingButton>
        </Stack>
      </AuthModal>

      {/* Reset Password */}
      <AuthModal
        isOpen={isResetPasswordOpen}
        onClose={() => setIsResetPasswordOpen(false)}
        type="message"
      >
        <Typography className={authModalClasses.header} mb={2}>
          Reset my password
        </Typography>
        <Typography className={authModalClasses.message}>
          Are you sure you want to reset your password? A link to create a new
          password will be emailed to your email.
        </Typography>
        <Typography className={authModalClasses.message}>
          Note: Clicking “Reset” will sign out of this session.
        </Typography>
        <Stack direction="row" justifyContent="flex-end" spacing={2} mt={2}>
          <Button
            variant="contained"
            className={authModalClasses.cancelButton}
            onClick={() => setIsResetPasswordOpen(false)}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            className={authModalClasses.submitButton}
            onClick={handleResetPassword}
            loading={isResetPasswordLoading}
          >
            Reset
          </LoadingButton>
        </Stack>
      </AuthModal>
    </>
  );
};

export default NavMenu;
